import React, { useState, useEffect } from 'react';
import { RankingSnapshot, InvoiceAverages, RankingWeightsConfig, apiService } from '../services/api';
import ScoreBreakdown from './ScoreBreakdown';
import DpdTrend from './DpdTrend';
import PenaltyBreakdown from './PenaltyBreakdown';
import SalesDataTab from './SalesDataTab';
import PenaltySimulator from './PenaltySimulator';

interface ScoreDetailsModalProps {
  show: boolean;
  onHide: () => void;
  ranking: RankingSnapshot | null;
  weights?: RankingWeightsConfig['weights'];
}

type TabType = 'score' | 'penalties' | 'trends' | 'context' | 'sales' | 'simulator';

const ScoreDetailsModal: React.FC<ScoreDetailsModalProps> = ({ show, onHide, ranking, weights }) => {
  const [activeTab, setActiveTab] = useState<TabType>('sales');
  const [invoiceAverages, setInvoiceAverages] = useState<InvoiceAverages | null>(null);
  const [loadingInvoiceData, setLoadingInvoiceData] = useState(false);

  // Fetch invoice averages when modal opens
  useEffect(() => {
    if (show && ranking?.sku) {
      console.log('Fetching invoice averages for SKU:', ranking.sku);
      setLoadingInvoiceData(true);
      apiService.getInvoiceAverages(ranking.sku)
        .then(data => {
          console.log('Invoice averages data received:', data);
          setInvoiceAverages(data);
        })
        .catch(error => {
          console.error('Error fetching invoice averages:', error);
          console.error('Error response:', error.response);
          if (error.response?.status === 404) {
            console.log('404 - No data found for SKU');
          }
          setInvoiceAverages(null);
        })
        .finally(() => {
          setLoadingInvoiceData(false);
        });
    }
  }, [show, ranking?.sku]);

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && show) {
        onHide();
      }
    };
    
    if (show) {
      document.addEventListener('keydown', handleEsc);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [show, onHide]);

  if (!ranking) return null;

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return `€${value.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number | null, decimals = 0): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('pt-PT', { 
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals 
    });
  };

  const formatPercentage = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className={`modal fade ${show ? 'show' : ''}`}
         style={{ display: show ? 'block' : 'none' }}
         tabIndex={-1}
         onClick={onHide}>
      <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Análise Detalhada - {ranking.sku}
              <span className="badge bg-primary ms-2">#{ranking.ranking_position}</span>
            </h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>
          
          <div className="modal-body">
            {/* Tabs */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'score' ? 'active' : ''}`}
                  onClick={() => setActiveTab('score')}
                >
                  <i className="bi bi-calculator me-1"></i>
                  Score
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'penalties' ? 'active' : ''}`}
                  onClick={() => setActiveTab('penalties')}
                >
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Penalizações
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'trends' ? 'active' : ''}`}
                  onClick={() => setActiveTab('trends')}
                >
                  <i className="bi bi-graph-up me-1"></i>
                  Tendências
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'context' ? 'active' : ''}`}
                  onClick={() => setActiveTab('context')}
                >
                  <i className="bi bi-info-circle me-1"></i>
                  Contexto
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'sales' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sales')}
                >
                  <i className="bi bi-cart-check me-1"></i>
                  Vendas
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'simulator' ? 'active' : ''}`}
                  onClick={() => setActiveTab('simulator')}
                >
                  <i className="bi bi-lightning-charge me-1"></i>
                  Simulador
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Score Tab */}
              {activeTab === 'score' && (
                <div className="tab-pane fade show active">
                  <ScoreBreakdown ranking={ranking} compact={false} weights={weights} />
                </div>
              )}

              {/* Penalties Tab */}
              {activeTab === 'penalties' && (
                <div className="tab-pane fade show active">
                  <PenaltyBreakdown ranking={ranking} compact={false} />
                </div>
              )}

              {/* Trends Tab */}
              {activeTab === 'trends' && (
                <div className="tab-pane fade show active">
                  <DpdTrend ranking={ranking} compact={false} />
                  
                  {/* Análise de mudança de preço */}
                  {ranking.mudanca_preco_pct && ranking.mudanca_preco_pct !== 0 && (
                    <div className="mt-4">
                      <h6 className="mb-3">Análise de Mudança de Preço</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="card">
                            <div className="card-body">
                              <h6 className="card-subtitle mb-2 text-muted">Mudança de Preço</h6>
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div className="small text-muted">De:</div>
                                  <div>{formatCurrency(ranking.preco_anterior)}</div>
                                </div>
                                <i className="bi bi-arrow-right mx-2"></i>
                                <div>
                                  <div className="small text-muted">Para:</div>
                                  <div>{formatCurrency(ranking.preco_atual)}</div>
                                </div>
                              </div>
                              <div className="mt-2 text-center">
                                <span className={`badge ${ranking.mudanca_preco_pct > 0 ? 'bg-danger' : 'bg-success'}`}>
                                  {formatPercentage(ranking.mudanca_preco_pct)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="card">
                            <div className="card-body">
                              <h6 className="card-subtitle mb-2 text-muted">Impacto Previsto no DPD</h6>
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div className="small text-muted">DPD Esperado Anterior:</div>
                                  <div>{formatNumber(ranking.dpd_esperado_anterior, 2)}</div>
                                </div>
                                <i className="bi bi-arrow-right mx-2"></i>
                                <div>
                                  <div className="small text-muted">DPD Esperado Atual:</div>
                                  <div>{formatNumber(ranking.dpd_esperado_atual, 2)}</div>
                                </div>
                              </div>
                              <div className="mt-2 text-center">
                                <span className={`badge ${ranking.impacto_previsto_dpd_pct! > 0 ? 'bg-success' : 'bg-danger'}`}>
                                  {formatPercentage(ranking.impacto_previsto_dpd_pct)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Context Tab */}
              {activeTab === 'context' && (
                <div className="tab-pane fade show active">
                  <div className="row">
                    {/* Informações do Produto */}
                    <div className="col-md-6">
                      <h6 className="mb-3">Informações do Produto</h6>
                      <div className="mb-3">
                        <label className="text-muted small">Categoria:</label>
                        <div>{ranking.categoria_nome || '-'}</div>
                        <div className="small text-muted">Código: {ranking.categoria_codigo || '-'}</div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Marca:</label>
                        <div>{ranking.marca_nome || '-'}</div>
                        <div className="small text-muted">Código: {ranking.marca_codigo || '-'}</div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Preço Atual:</label>
                        <div className="h5">{formatCurrency(ranking.preco_atual)}</div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Stock Disponível:</label>
                        <div className="h5">{formatNumber(ranking.stock_quantity)} unidades</div>
                      </div>
                    </div>

                    {/* Performance de Mercado */}
                    <div className="col-md-6">
                      <h6 className="mb-3">Performance de Mercado</h6>
                      <div className="mb-3">
                        <label className="text-muted small">Receita da Categoria (7d):</label>
                        <div>{formatCurrency(ranking.category_revenue)}</div>
                        <div className="progress mt-1" style={{ height: '10px' }}>
                          <div 
                            className="progress-bar bg-warning" 
                            role="progressbar" 
                            style={{ width: `${(ranking.norm_category || 0) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Receita da Marca (7d):</label>
                        <div>{formatCurrency(ranking.brand_revenue)}</div>
                        <div className="progress mt-1" style={{ height: '10px' }}>
                          <div 
                            className="progress-bar bg-info" 
                            role="progressbar" 
                            style={{ width: `${(ranking.norm_brand || 0) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="text-muted small">Potencial de Vendas:</label>
                        <div>{formatCurrency(ranking.potential_sales)}</div>
                        <div className="small text-muted">(Preço × Stock)</div>
                      </div>
                    </div>
                  </div>

                  {/* Métricas de Performance */}
                  <div className="mt-4">
                    <h6 className="mb-3">Métricas de Performance (7 dias)</h6>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body p-2">
                            <div className="small text-muted">Receita Total</div>
                            <div className="h6 mb-0">{formatCurrency(ranking.revenue_total)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body p-2">
                            <div className="small text-muted">Unidades Vendidas</div>
                            <div className="h6 mb-0">{formatNumber(ranking.quantity_total)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body p-2">
                            <div className="small text-muted">Ticket Médio</div>
                            <div className="h6 mb-0">
                              {ranking.quantity_total && ranking.quantity_total > 0 
                                ? formatCurrency((ranking.revenue_total || 0) / ranking.quantity_total)
                                : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card text-center">
                          <div className="card-body p-2">
                            <div className="small text-muted">Score Elasticidade</div>
                            <div className="h6 mb-0">{formatNumber(ranking.elasticity_score, 2)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sales Tab */}
              {activeTab === 'sales' && (
                <div className="tab-pane fade show active">
                  <SalesDataTab
                    ranking={ranking}
                    invoiceAverages={invoiceAverages}
                    loadingInvoiceData={loadingInvoiceData}
                  />
                </div>
              )}

              {/* Simulator Tab */}
              {activeTab === 'simulator' && (
                <div className="tab-pane fade show active">
                  <PenaltySimulator ranking={ranking} />
                </div>
              )}
            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onHide}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreDetailsModal;